import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ReactComponent as BackArrowIcon } from "../../utilities/images/study_dashboard/back_to_learning_arrow.svg";
import ExploreCoursesBannerCarousel from "../DashboardCourses/components/ExploreCoursesBannerCarousel";
import HandbookCart from "../../containers/logged_user_components/user_store/user_store_content_component/store_components/handbook/handbookcart/handbookcart/HandbookCart";
import DashboardHandbookCard from "./components/DashboardHandbookCard";
import {
  useDashboardHandbookListQuery,
  useDashboardHomeSliderBannersQuery,
} from "../StudyDashboard/hooks/useStudyDashboardQueries";
import { navigateToHandbookPage } from "../../custom_utilities/dashboardBannerNavigation";
import uiActions from "../../redux/ui/action";
import { setStoreCurrentHandbookDetails } from "../../redux/handbook/HandbookSlice";
import { buyHandbookNow } from "./utils/handbookCheckout";
import useDashboardScrollRestore from "../DashboardCourses/hooks/useDashboardScrollRestore";
import {
  clearExploreBooksScrollState,
  EXPLORE_BOOKS_PATH,
  getExploreHandbookListAnchorId,
  readExploreBooksScrollState,
  saveExploreBooksScrollState,
} from "../DashboardCourses/utils/exploreCoursesScrollRestore";
import "./DashboardBooks.css";
const HANDBOOK_CATEGORIES = [
  { category_name: "ALL", id: "" },
  { category_name: "NEET", id: "1" },
  { category_name: "JEE", id: "6" },
  { category_name: "Foundation", id: "7" },
];

const getDefaultFilterId = (courseName) => {
  const normalized = String(courseName ?? "").toLowerCase();
  if (normalized.includes("jee")) return "6";
  if (normalized.includes("neet")) return "1";
  return "7";
};

const DashboardBooks = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const courseName = useSelector(
    (state) => state.userPreference?.courseDetails?.[0]?.course
  );
  const pendingScrollRestoreRef = useRef(readExploreBooksScrollState());
  const [highlightedAnchorId, setHighlightedAnchorId] = useState(
    () => pendingScrollRestoreRef.current?.anchorId ?? null
  );
  const [activeFilterId, setActiveFilterId] = useState(() => {
    const savedFilterId = pendingScrollRestoreRef.current?.activeFilterId;
    if (savedFilterId !== undefined && savedFilterId !== null) {
      return savedFilterId;
    }
    return getDefaultFilterId(courseName);
  });
  const hasUserSelectedFilter = useRef(Boolean(pendingScrollRestoreRef.current));

  useEffect(() => {
    if (hasUserSelectedFilter.current || !courseName) return;
    setActiveFilterId(getDefaultFilterId(courseName));
  }, [courseName]);

  const selectFilter = (id) => {
    hasUserSelectedFilter.current = true;
    setHighlightedAnchorId(null);
    setActiveFilterId(id);
  };
  const handbookListQuery = useDashboardHandbookListQuery(activeFilterId);
  const sliderBannersQuery = useDashboardHomeSliderBannersQuery();

  useDashboardScrollRestore({
    isReady: !handbookListQuery.isLoading,
    restoreTrigger: activeFilterId,
    readScrollState: readExploreBooksScrollState,
    clearScrollState: clearExploreBooksScrollState,
  });

  useEffect(() => {
    if (handbookListQuery.isLoading) {
      dispatch(uiActions.startLoading());
      return;
    }
    dispatch(uiActions.finishLoading());
  }, [dispatch, handbookListQuery.isLoading]);

  const books = handbookListQuery.data ?? [];
  const sliderBanners = sliderBannersQuery.data ?? [];

  const saveScrollBeforeLeave = useCallback(
    (anchorId) => {
      setHighlightedAnchorId(anchorId);
      saveExploreBooksScrollState({
        anchorId,
        activeFilterId,
      });
    },
    [activeFilterId]
  );

  const openBook = (book) => {
    if (!book?.p_id) return;
    saveScrollBeforeLeave(getExploreHandbookListAnchorId(book.p_id));
    dispatch(setStoreCurrentHandbookDetails(book));
    navigateToHandbookPage(history, book.p_id, {
      returnTo: EXPLORE_BOOKS_PATH,
    });
  };

  const buyBook = (book) => {
    saveScrollBeforeLeave(getExploreHandbookListAnchorId(book.p_id));
    buyHandbookNow(history, dispatch, book, { returnTo: EXPLORE_BOOKS_PATH });
  };
  if (handbookListQuery.isLoading) {
    return <div className="dashboard-books dashboard-books--loading" aria-busy="true" />;
  }

  return (
    <div className="dashboard-books">
      <ExploreCoursesBannerCarousel
        banners={sliderBanners}
        returnTo={EXPLORE_BOOKS_PATH}
      />

      <div className="dashboard-books__header">
        <button
          type="button"
          className="dashboard-books__go-back"
          onClick={() => history.push("/dashboard/courses")}
        >
          <BackArrowIcon className="dashboard-books__go-back-icon" aria-hidden="true" />
          <span>Back</span>
        </button>
        <HandbookCart />
      </div>
      <div className="dashboard-books__filters" role="tablist" aria-label="Handbook categories">
        {HANDBOOK_CATEGORIES.map((category) => (
          <button
            key={category.category_name}
            type="button"
            role="tab"
            aria-selected={activeFilterId === category.id}
            className={`dashboard-books__filter${
              activeFilterId === category.id ? " is-active" : ""
            }`}
            onClick={() => selectFilter(category.id)}
          >
            {category.category_name}
          </button>
        ))}
      </div>

      <section className="dashboard-books__grid" aria-label="Handbooks">
        {books.length ? (
          books.map((book) => (
            <DashboardHandbookCard
              key={book.p_id}
              book={book}
              highlightedAnchorId={highlightedAnchorId}
              onOpen={openBook}
              onBuy={buyBook}
            />
          ))
        ) : (
          <p className="dashboard-books__empty">No books available right now.</p>
        )}
      </section>
    </div>
  );
};

export default DashboardBooks;
